<template>
  <PropertyNotFound
    v-if="isMissing"
    icon="building-06"
    title="This building no longer exists"
    description="It was deleted, or the link points at a building that was never here."
  />

  <EmptyPagePlaceholder
    v-else-if="status === 'error'"
    title="Could not load the building"
    description="Please reload the page or try again later"
  />

  <PageContainer v-else>
    <View direction="column" gap="xl">
      <!-- The header's shapes while the building loads; the open tab draws its own -->
      <View v-if="!building" direction="column" gap="md" class="header" aria-busy="true" data-testid="header-skeleton">
        <Skeleton width="10rem" height="1rem" />
        <View gap="md" align="center" justify="space-between" wrap="wrap">
          <View gap="md" align="center">
            <Skeleton :width="screenMobile ? '3rem' : '5rem'" :height="screenMobile ? '3rem' : '5rem'" />
            <View direction="column" gap="sm">
              <Skeleton width="14rem" height="2rem" />
              <Skeleton width="16rem" height="1rem" />
            </View>
          </View>
          <View v-if="showActions" gap="sm" class="actions">
            <Skeleton width="8rem" height="2.5rem" />
            <Skeleton width="8rem" height="2.5rem" />
          </View>
        </View>
      </View>

      <View v-else direction="column" gap="md" class="header">
        <Breadcrumbs
          :items="[
            {
              label: building.portfolio?.name ?? 'All properties',
              to: portfolioRoute(building.portfolio?.id, 'buildings'),
            },
            { label: building.name },
          ]"
        />

        <View gap="md" align="center" justify="space-between" wrap="wrap">
          <View gap="md" align="center" class="identity">
            <BuildingCover :building="building" :size="screenMobile ? 'sm' : 'md'" />

            <View direction="column" gap="xs" class="identity">
              <View gap="sm" align="center" wrap="wrap">
                <Text :variant="screenMobile ? 'h2' : 'h1'">{{ building.name }}</Text>
                <Badge icon="building-06" label="Building" variant="grey" size="small" />
              </View>
              <Text variant="body-medium" color="secondary">{{ buildingAddressLine(building.location) }}</Text>
            </View>
          </View>

          <!-- The units tab's actions: the other tabs are where "Edit building" leads -->
          <View v-if="showActions" gap="sm" wrap="wrap" class="actions">
            <Button variant="outline" size="medium" :to="buildingDetailsRoute(building.id)" data-testid="edit-building">
              <Icon name="pencil" size="small" />
              <span>Edit building</span>
            </Button>
            <!-- Beside Add unit, the one place both ways of getting a unit sit together; adding stays the main one -->
            <Button
              variant="outline"
              size="medium"
              data-testid="open-attach-homes"
              @click="attachHomesModalRef?.open()"
            >
              <Icon name="home-03" size="small" />
              <span>{{ screenMobile ? 'Attach home' : 'Attach existing home' }}</span>
            </Button>
            <Button size="medium" data-testid="open-add-unit" @click="addUnitModalRef?.open()">
              <Icon name="plus" size="small" />
              <span>Add unit</span>
            </Button>
          </View>
        </View>
      </View>

      <!-- The open tab: its page reads the building from the route, like this header does -->
      <RouterView />
    </View>
    <AddUnitModal v-if="building" ref="addUnitModalRef" :building-id="building.id" />
    <AttachHomesModal v-if="building" ref="attachHomesModalRef" :building="building" />
  </PageContainer>
</template>

<script setup lang="ts">
import { computed, provide, ref } from 'vue';
import { useRoute } from 'vue-router';

import {
  Badge,
  Breadcrumbs,
  Button,
  EmptyPagePlaceholder,
  Icon,
  PageContainer,
  Skeleton,
  Text,
  View,
  screenMobile,
} from '@tallo/design-system';
import { AppRouteNames, buildingDetailsRoute, portfolioRoute } from '@tallo/investor/navigation';
import {
  PropertyNotFound,
  buildingAddressLine,
  buildingDetailsHolder,
  detailsHolderKey,
  useBuildingQuery,
  useSaveBuildingSection,
  useSaveBuildingUnitDefault,
} from '@tallo/investor/property';

import AddUnitModal from './components/add-unit-modal.vue';
import AttachHomesModal from './components/attach-homes-modal.vue';
import BuildingCover from './components/building-cover.vue';
import { unitActionsKey } from './unit-actions.key';

const route = useRoute();
// Read from the route every time: moving from one building's page to another's keeps this component
const { data: building, status, isMissing } = useBuildingQuery(() => route.params.buildingId as string);
const addUnitModalRef = ref<InstanceType<typeof AddUnitModal>>();
const attachHomesModalRef = ref<InstanceType<typeof AttachHomesModal>>();
const { mutateAsync: saveSection } = useSaveBuildingSection();
const { mutateAsync: saveUnitDefault } = useSaveBuildingUnitDefault();

const showActions = computed(() => route.name === AppRouteNames.INVESTOR_BUILDING_UNITS);

provide(unitActionsKey, {
  addUnit: () => addUnitModalRef.value?.open(),
  attachHomes: () => attachHomesModalRef.value?.open(),
});
// The detail forms on the details tab edit the building's sections and defaults, not a property's
provide(
  detailsHolderKey,
  buildingDetailsHolder(
    building,
    (section, values) => saveSection({ buildingId: route.params.buildingId as string, section, values }),
    (section, values) => saveUnitDefault({ buildingId: route.params.buildingId as string, section, values }),
  ),
);
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

// A long building name wraps rather than widening the page
.header,
.identity {
  min-width: 0;
  overflow-wrap: anywhere;
}

// On a phone the actions take the rows under the name, two to a row
.actions {
  @include mobile {
    width: 100%;

    > * {
      flex: 1 1 40%;
    }
  }
}
</style>
